<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { isLabelInCategory } from 'dashboard/constants/labelCategories';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const store = useStore();
const { isAdmin } = useAdmin();

const allLabels = useMapGetter('labels/getLabels');
const uiFlags = useMapGetter('contactLabels/getUIFlags');

// Titles currently saved on the contact.
const savedLabels = computed(() =>
  store.getters['contactLabels/getContactLabels'](props.contactId)
);
// Account labels assignable to a contact (global or contact-scoped).
const accountLabels = computed(() =>
  (allLabels.value || []).filter(label => isLabelInCategory(label, 'contact'))
);
// Labels currently on the contact (shown as chips), regardless of category.
const activeLabels = computed(() =>
  (allLabels.value || []).filter(label =>
    savedLabels.value.includes(label.title)
  )
);

const showDropdown = ref(false);
const toggleLabels = () => {
  showDropdown.value = !showDropdown.value;
};
const closeDropdown = () => {
  showDropdown.value = false;
};

const updateLabels = labels =>
  store.dispatch('contactLabels/update', {
    contactId: props.contactId,
    labels,
  });
const addLabel = value =>
  updateLabels([...new Set([...savedLabels.value, value.title])]);
const removeLabel = value =>
  updateLabels(savedLabels.value.filter(title => title !== value));

const fetchLabels = () => {
  if (props.contactId) store.dispatch('contactLabels/get', props.contactId);
};

onMounted(() => {
  store.dispatch('labels/get');
  fetchLabels();
});
watch(() => props.contactId, fetchLabels);
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div v-if="!uiFlags.isFetching" class="contact-conversation--list">
      <div
        v-on-clickaway="closeDropdown"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdown"
      >
        <AddLabel @add="toggleLabels" />
        <woot-label
          v-for="label in activeLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="max-w-[calc(100%-0.5rem)]"
          @remove="removeLabel"
        />

        <div
          :class="{
            'block visible': showDropdown,
            'hidden invisible': !showDropdown,
          }"
          class="border rounded-lg bg-n-alpha-3 top-6 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
        >
          <LabelDropdown
            v-if="showDropdown"
            :account-labels="accountLabels"
            :selected-labels="savedLabels"
            :allow-creation="isAdmin"
            @add="addLabel"
            @remove="removeLabel"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }
}
</style>
